import React, { useState } from 'react';
import { useInventory } from '../context/InventoryContext';
import BulkUpload from '../components/BulkUpload';
import { FaEdit, FaTrash, FaPlus, FaCog, FaSearch } from 'react-icons/fa';
import Pagination from '../components/Pagination';
import { toast } from 'react-toastify';

const CompanyDetails = () => {
  const { companies, addCompany, updateCompany, deleteCompany, getVehicleCountByCompany } = useInventory();
  const [form, setForm] = useState({ name: '', address: '', contactPerson: '', phone: '' });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  
  // Store original values for comparison when editing
  const [originalFormValues, setOriginalFormValues] = useState(null);
  
  // State for config modal
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configSearchQuery, setConfigSearchQuery] = useState('');
  const [selectedCompaniesForLogAccess, setSelectedCompaniesForLogAccess] = useState(() => {
    const saved = localStorage.getItem('logPageEnabledCompanies');
    return saved ? JSON.parse(saved) : [];
  });

  const resetForm = () => {
    setForm({ name: '', address: '', contactPerson: '', phone: '' });
    setEditingId(null);
    setShowForm(false);
    setErrors({});
    setOriginalFormValues(null);
  };

  // Helper function to check if any field was actually changed
  const hasChanges = (original, current) => {
    if (!original) return false;
    // Compare all fields
    const fieldsToCompare = ['name', 'address', 'contactPerson', 'phone'];
    
    for (let field of fieldsToCompare) {
      const originalValue = (original[field] || '').trim();
      const currentValue = (current[field] || '').trim();
      if (originalValue !== currentValue) {
        return true;
      }
    }
    
    return false;
  };

  const filteredCompanies = companies.filter(company => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      company.name.toLowerCase().includes(q) ||
      (company.contactPerson || '').toLowerCase().includes(q) ||
      (company.phone || '').includes(q)    
    );
  });

  // Filter companies for config modal based on search
  const configFilteredCompanies = companies.filter(company => {
    if (!configSearchQuery.trim()) return true;
    const q = configSearchQuery.toLowerCase();
    return (
      company.name.toLowerCase().includes(q) ||
      (company.contactPerson || '').toLowerCase().includes(q) ||
      (company.phone || '').includes(q)
    );
  });

  const totalItems = filteredCompanies.length;
  const paginatedCompanies = filteredCompanies.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) {
      newErrors.name = 'Company name is required.';
    } else {
      const duplicate = companies.find(
        c => c.id !== editingId && c.name.toLowerCase() === form.name.trim().toLowerCase()
      );
      if (duplicate) newErrors.name = 'Company name already exists.';
    }
    if (!/^\d{10}$/.test(form.phone)) newErrors.phone = 'Phone must be exactly 10 digits.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    if (editingId) {
      // Check if anything was actually changed
      if (!hasChanges(originalFormValues, form)) {
        setErrors({});
        resetForm();
        toast.info('No changes were made to the company.');
        return;
      }
      
      updateCompany(editingId, form);
      // Show notification for edit
      toast.success(`✅ Company "${form.name.trim()}" has been updated successfully!`);
    } else {
      // Adding new company
      addCompany(form);
      // Show notification for add
      toast.success(`✅ New company "${form.name.trim()}" has been created successfully!`);
    }
    resetForm();
  };

  const handleEdit = (company) => {
    const formValues = {
      name: company.name,
      address: company.address || '',
      contactPerson: company.contactPerson || '',
      phone: company.phone || '',
    };
    
    // Store original values for comparison
    setOriginalFormValues({
      name: company.name,
      address: company.address || '',
      contactPerson: company.contactPerson || '',
      phone: company.phone || '',
    });
    
    setForm(formValues);
    setEditingId(company.id);
    setShowForm(true);
    setErrors({});
  };

  const handleDelete = (companyId) => {
    // Find company name for notification
    const companyToDelete = companies.find(c => c.id === companyId);
    const companyName = companyToDelete?.name || 'Unknown';
    
    if (window.confirm(`Are you sure you want to delete "${companyName}"?`)) {
      deleteCompany(companyId);
      // Show notification for delete
      toast.success(`Company "${companyName}" has been deleted successfully!`);
    }
  };

  const validateRow = (row, index, allRows) => {
    const errs = [];
    const name = (row.name || '').trim();
    if (!name) {
      errs.push({ column: 'Company Name', message: 'Required' });
    } else {
      if (companies.some(c => c.name.toLowerCase() === name.toLowerCase())) {
        errs.push({ column: 'Company Name', message: 'Company name already exists in database' });
      }
      const earlierDuplicate = allRows.slice(0, index).some(
        r => (r.name || '').trim().toLowerCase() === name.toLowerCase()
      );
      if (earlierDuplicate) {
        errs.push({ column: 'Company Name', message: 'Duplicate name within this file' });
      }
    }
    if (row.phone && !/^\d{10}$/.test(row.phone)) {
      errs.push({ column: 'Phone', message: 'Must be exactly 10 digits' });
    }
    return errs;
  };

  // Config functions
  const handleConfigClick = () => {
    setConfigSearchQuery('');
    setShowConfigModal(true);
  };

  const handleCompanyToggle = (companyId) => {
    setSelectedCompaniesForLogAccess(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  const handleSelectAll = () => {
    // Select only the filtered companies (visible in search results)
    const filteredCompanyIds = configFilteredCompanies.map(c => c.id);
    setSelectedCompaniesForLogAccess(prev => {
      const newSelection = [...new Set([...prev, ...filteredCompanyIds])];
      return newSelection;
    });
  };

  const handleDeselectAll = () => {
    // Deselect only the filtered companies (visible in search results)
    const filteredCompanyIds = configFilteredCompanies.map(c => c.id);
    setSelectedCompaniesForLogAccess(prev => prev.filter(id => !filteredCompanyIds.includes(id)));
  };

  const handleSelectAllCompanies = () => {
    // Select ALL companies (not just filtered)
    setSelectedCompaniesForLogAccess(companies.map(c => c.id));
  };

  const handleDeselectAllCompanies = () => {
    // Deselect ALL companies
    setSelectedCompaniesForLogAccess([]);
  };

  const handleSaveConfig = () => {
    localStorage.setItem('logPageEnabledCompanies', JSON.stringify(selectedCompaniesForLogAccess));
    setShowConfigModal(false);
    window.dispatchEvent(new Event('logPageAccessChanged'));
    toast.success(`✅ Log page access configured for ${selectedCompaniesForLogAccess.length} companies.`);
  };

  const handleRemoveAllAccess = () => {
    setSelectedCompaniesForLogAccess([]);
    localStorage.setItem('logPageEnabledCompanies', JSON.stringify([]));
    setShowConfigModal(false);
    window.dispatchEvent(new Event('logPageAccessChanged'));
    toast.info('All company log access has been removed.');
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Company Details</h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="btn-primary flex items-center space-x-2"
          >
            <FaPlus />
            <span>Add Company</span>
          </button>
          
          <button
            onClick={handleConfigClick}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-xl flex items-center space-x-2 transition-colors"
          >
            <FaCog />
            <span>Config Log Access</span>
          </button>
          
          <BulkUpload
            title="Company"
            downloadLabel="Download Add Template"
            uploadLabel="Bulk Add"
            downloadData={companies.map(c => ({
              name: c.name,
              address: c.address,
              contactPerson: c.contactPerson,
              phone: c.phone
            }))}
            columns={[
              { key: 'name', label: 'Company Name' },
              { key: 'address', label: 'Address' },
              { key: 'contactPerson', label: 'Contact Person' },
              { key: 'phone', label: 'Phone' },
            ]}
            sampleRow={{ name: 'Example Co', address: '123 St', contactPerson: 'John', phone: '9999999999' }}
            onUpload={(rows) => rows.forEach(row => addCompany(row))}
            validateRow={validateRow}
          />
        </div>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name, contact person, phone…"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full max-w-md rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>
          
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Address</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact Person</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vehicles</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Log Access</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {paginatedCompanies.map(c => (
                <tr key={c.id} className="hover:bg-indigo-50/50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{c.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{c.address || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{c.contactPerson || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{c.phone || '-'}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-indigo-600">{getVehicleCountByCompany(c.id)}</td>
                  <td className="px-4 py-3 text-sm">
                    {selectedCompaniesForLogAccess.includes(c.id) ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Enabled</span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">Disabled</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex space-x-2">
                      <button onClick={() => handleEdit(c)} className="text-indigo-600 hover:text-indigo-900 transition">
                        <FaEdit />
                      </button>
                      <button onClick={() => handleDelete(c.id)} className="text-red-500 hover:text-red-700 transition">
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                 </tr>
              ))}
              {paginatedCompanies.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-500">No companies added.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />

      {/* Config Modal with Search */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-3xl mx-auto my-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Configure Log Page Access</h2>
            <p className="text-sm text-gray-600 mb-4">
              Select companies that should have access to the Log Management page. 
              When users from these companies log in, they will automatically see the Log Page.
            </p>
            
            {/* Search Bar for Config Modal */}
            <div className="mb-4 relative">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search companies by name, contact person, or phone..."
                  value={configSearchQuery}
                  onChange={(e) => setConfigSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {configSearchQuery && (
                <p className="text-xs text-gray-500 mt-1">
                  Found {configFilteredCompanies.length} company(ies) matching "{configSearchQuery}"
                </p>
              )}
            </div>

            <div className="mb-4 flex justify-between items-center flex-wrap gap-2">
              <p className="text-sm font-medium text-gray-700">
                Selected: {selectedCompaniesForLogAccess.length} / {companies.length} companies
              </p>
              <div className="space-x-2 flex flex-wrap gap-2">
                <button
                  onClick={handleSelectAll}
                  className="px-3 py-1 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition"
                >
                  Select Filtered ({configFilteredCompanies.length})
                </button>
                <button
                  onClick={handleDeselectAll}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                >
                  Deselect Filtered
                </button>
                <button
                  onClick={handleSelectAllCompanies}
                  className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
                >
                  Select All ({companies.length})
                </button>
                <button
                  onClick={handleDeselectAllCompanies}
                  className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                >
                  Deselect All
                </button>
              </div>
            </div>

            <div className="border rounded-xl overflow-hidden mb-6">
              <div className="max-h-96 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-12">
                        Select
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Company Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Contact Person
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Phone
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {configFilteredCompanies.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-4 py-8 text-center text-sm text-gray-500">
                          {configSearchQuery ? `No companies found matching "${configSearchQuery}"` : 'No companies available. Please add companies first.'}
                        </td>
                      </tr>
                    ) : (
                      configFilteredCompanies.map(company => (
                        <tr key={company.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedCompaniesForLogAccess.includes(company.id)}
                              onChange={() => handleCompanyToggle(company.id)}
                              className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{company.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{company.contactPerson || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{company.phone || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button
                onClick={() => setShowConfigModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveAllAccess}
                className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition"
              >
                Remove All Access
              </button>
              <button
                onClick={handleSaveConfig}
                className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-lg mx-auto my-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{editingId ? 'Edit Company' : 'Add Company'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                <input className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" value={form.contactPerson} onChange={e => setForm({...form, contactPerson: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (10 digits) *</label>
                <input className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" value={form.phone} onChange={e => setForm({...form, phone: e.target.value.replace(/\D/g, '')})} maxLength={10} />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={resetForm} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">{editingId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDetails;